import { AsyncPipe } from '@angular/common';
import { Component, computed, DestroyRef, inject, Injector, signal, Signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogConfig,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatError, MatFormField, MatHint, MatInput, MatLabel } from '@angular/material/input';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import dayjs from 'dayjs';
import Mustache from 'mustache';
import { debounceTime, distinctUntilChanged, filter, map, startWith, take } from 'rxjs/operators';
import { EFieldTypes, ESqlOperators } from 'scw-digital-boards-widgets';
import { EDialogWidth } from '../../../../../core/constants/ui.constants';
import { TrimmedRequiredValidator } from '../../../../../core/validators/trimmed-required.validator';
import {
  ADVANCED_FILTER_FIELD_VALUE_TYPES,
  ADVANCED_FILTER_FIELDS,
  EValueType,
} from '../../../../../shared/components/advanced-filter/advanced-filter-fields.constants';
import { TAdvancedFilterConfig } from '../../../../../shared/components/advanced-filter/advanced-filter.interface';
import { ErrorMessagesComponent } from '../../../../../shared/components/error-messages/error-messages.component';
import { IDropdownOption, MatDropdown } from '../../../../../shared/components/mat-dropdown/mat-dropdown.component';
import { IDropdownSettings } from '../../../../../shared/components/mat-dropdown/scw-mat-dropdown.interface';
import { ELoadStatus } from '../../../../../state/state.interface';
import { FormUtilitiesService } from '../../../../../utilities/form-utilities.service';
import { ESnackbar, SnackbarService } from '../../../../../utilities/snackbar.service';
import {
  DEFAULT_ENDPOINT_PARAMETER_DATE_FORMAT,
  DEFAULT_ENDPOINT_PARAMETER_DATETIME_FORMAT,
  EDatePart,
  ENDPOINT_PARAMETER_DATE_FORMATS,
  ENDPOINT_PARAMETER_DATETIME_FORMATS,
  EParamLocation,
  EShiftOutput,
  IUrlParamsTemplateAddPayload,
  IUrlParamsTemplateTag,
  IUrlParamsTemplateTagPayload,
} from '../endpoint-parameters.interface';
import { IEndpointParametersAddEditDialogData } from './endpoint-parameters-add-edit-dialog.interface';
import { EndpointParametersAddEditDialogStore } from './endpoint-parameters-add-edit-dialog.store';

interface ITagFormGroup {
  tag: FormControl<string>;
  field: FormControl<IDropdownOption<string>[]>;
  operator: FormControl<IDropdownOption<ESqlOperators>[]>;
  splitBy: FormControl<string>;
  isRequired: FormControl<IDropdownOption<boolean>[]>;
  valueType: FormControl<IDropdownOption<EValueType>[]>;
  valuePrefix: FormControl<string>;
  valueSuffix: FormControl<string>;
  dateFormat: FormControl<IDropdownOption<string>[]>;
  datePart: FormControl<IDropdownOption<EDatePart>[]>;
  shiftOutput: FormControl<IDropdownOption<EShiftOutput>[]>;
}

interface ITagInitialOptions {
  fieldOption: IDropdownOption<string>[];
  operatorOption: IDropdownOption<ESqlOperators>[];
  isRequiredOption: IDropdownOption<boolean>[];
  valueTypeOption: IDropdownOption<EValueType>[];
  dateFormatOption: IDropdownOption<string>[];
  datePartOption: IDropdownOption<EDatePart>[];
  shiftOutputOption: IDropdownOption<EShiftOutput>[];
}

type TTagFormGroupWithMeta = FormGroup<ITagFormGroup> & {
  readonly operatorOptions: Signal<IDropdownOption<ESqlOperators>[]>;
  readonly isInOperator: Signal<boolean>;
  readonly availableValueTypes: Signal<IDropdownOption<EValueType>[]>;
  readonly isDateField: Signal<boolean>;
  readonly isShiftPhraseOperator: Signal<boolean>;
  readonly isDatePartVisible: Signal<boolean>;
  readonly isShiftOutputVisible: Signal<boolean>;
  readonly isShiftTimesSelected: Signal<boolean>;
  readonly availableDateFormats: Signal<IDropdownOption<string>[]>;
  readonly isSplitByVisible: Signal<boolean>;
};

const DATE_FORMAT_EXAMPLE_DATE = '2025-12-31';
const DATETIME_FORMAT_EXAMPLE_DATE = '2025-12-31 23:59:59';
const AUTO_SYNC_IDLE_DELAY = 5000;

interface IQueryTemplateSegment {
  type: 'text' | 'tag';
  value: string;
}

type TMustacheTokenType = 'text' | 'name' | '&' | '#' | '^' | '/' | '!' | '>';

type TMustacheToken =
  | [TMustacheTokenType, string, number, number]
  | [TMustacheTokenType, string, number, number, TMustacheToken[], number, number?];

const SIMPLE_TOKEN_FORMATTERS: Partial<Record<TMustacheTokenType, (value: string) => IQueryTemplateSegment>> = {
  '!': (value) => ({ type: 'tag', value: `{{!${value}}}` }),
  '&': (value) => ({ type: 'tag', value: `{{&${value}}}` }),
  '>': (value) => ({ type: 'tag', value: `{{>${value}}}` }),
  name: (value) => ({ type: 'tag', value: `{{${value}}}` }),
  text: (value) => ({ type: 'text', value }),
};

@Component({
  imports: [
    AsyncPipe,
    ErrorMessagesComponent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    MatDialogContent,
    MatDialogTitle,
    MatError,
    MatFormField,
    MatHint,
    MatInput,
    MatLabel,
    ReactiveFormsModule,
    TranslatePipe,
    MatDropdown,
    MatIcon,
  ],
  providers: [EndpointParametersAddEditDialogStore],
  selector: 'app-endpoint-parameters-add-edit-dialog',
  styleUrl: './endpoint-parameters-add-edit-dialog.component.scss',
  templateUrl: './endpoint-parameters-add-edit-dialog.component.html',
})
export class EndpointParametersAddEditDialogComponent {
  public static dialogConfig: MatDialogConfig = {
    closeOnNavigation: true,
    disableClose: true,
    exitAnimationDuration: 0,
    maxWidth: '95vw',
    restoreFocus: false,
    width: EDialogWidth.large,
  };

  public readonly injectData = inject<IEndpointParametersAddEditDialogData>(MAT_DIALOG_DATA);
  public readonly store = inject(EndpointParametersAddEditDialogStore);
  private readonly matDialogRef = inject(MatDialogRef);
  private readonly translate = inject(TranslateService);
  private readonly snackbarService = inject(SnackbarService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  public readonly paramLocationDropdownOptions: IDropdownOption<EParamLocation>[] = [
    {
      id: EParamLocation.query,
      name: this.translate.instant('page.dataIntegrations.endpointParameters.paramLocation.query'),
    },
    {
      id: EParamLocation.body,
      name: this.translate.instant('page.dataIntegrations.endpointParameters.paramLocation.body'),
    },
    {
      id: EParamLocation.header,
      name: this.translate.instant('page.dataIntegrations.endpointParameters.paramLocation.header'),
    },
  ];

  private readonly operatorNames: Record<ESqlOperators, string> = Object.values(ESqlOperators).reduce(
    (acc, op) => {
      return {
        ...acc,
        [op]: this.translate.instant(
          op === ESqlOperators.$between ? 'advanceFilter.operator.range' : `advanceFilter.operator.${op}`,
        ),
      };
    },
    {} as Record<ESqlOperators, string>,
  );

  public readonly fieldDropdownOptions = computed<IDropdownOption<string>[]>(() => {
    return [
      ...Object.keys(ADVANCED_FILTER_FIELDS).map((field) => ({
        id: field,
        name: this.translate.instant(`advanceFilter.field.${field}`) as string,
      })),
      ...this.store
        .customFilters()
        .map((customFilter) => ({ id: `custom_${customFilter.id}`, name: customFilter.name })),
    ];
  });

  private readonly operatorOptionsByField = computed(() => {
    return new Map(
      (
        [
          ...Object.entries(ADVANCED_FILTER_FIELDS),
          ...this.store
            .customFilters()
            .map((customFilter) => [
              `custom_${customFilter.id}`,
              [{ operators: [ESqlOperators.$in, ESqlOperators.$eq], type: customFilter.typeBasedConfiguration.type }],
            ]),
        ] as [string, Pick<TAdvancedFilterConfig, 'type' | 'operators'>[]][]
      ).map(([field, operatorSet]) => [
        field,
        operatorSet.reduce(
          (reducePayload, { type, operators }) => [
            ...reducePayload,
            ...operators.map((operator) => ({
              id: operator,
              name: this.operatorNames[operator],
            })),
            ...((type === EFieldTypes.apiDropdown || type === EFieldTypes.predefined) &&
            (operators as ESqlOperators[]).includes(ESqlOperators.$in)
              ? [
                  {
                    id: ESqlOperators.$eq,
                    name: this.operatorNames.$eq,
                  },
                ]
              : []),
            ...(type === EFieldTypes.date && (operators as ESqlOperators[]).includes(ESqlOperators.$between)
              ? [
                  {
                    id: ESqlOperators.$eq,
                    name: this.translate.instant('advanceFilter.operator.single'),
                  },
                ]
              : []),
          ],
          [] as IDropdownOption<ESqlOperators>[],
        ),
      ]),
    );
  });

  private readonly emptyOperatorOptions: IDropdownOption<ESqlOperators>[] = [];

  public readonly isRequiredDropdownOptions: IDropdownOption<boolean>[] = [
    { id: false, name: this.translate.instant('button.no') },
    { id: true, name: this.translate.instant('button.yes') },
  ];

  public readonly valueTypeDropdownOptions: IDropdownOption<EValueType>[] = [
    { id: EValueType.raw, name: this.translate.instant('page.dataIntegrations.endpointParameters.valueType.raw') },
    {
      id: EValueType.display,
      name: this.translate.instant('page.dataIntegrations.endpointParameters.valueType.display'),
    },
  ];

  public readonly dateFormatDropdownOptions: IDropdownOption<string>[] = ENDPOINT_PARAMETER_DATE_FORMATS.map(
    (dateFormat) => ({
      id: dateFormat,
      name: `${dateFormat}  (${dayjs(DATE_FORMAT_EXAMPLE_DATE).format(dateFormat)})`,
    }),
  );

  public readonly dateTimeFormatDropdownOptions: IDropdownOption<string>[] = ENDPOINT_PARAMETER_DATETIME_FORMATS.map(
    (dateFormat) => ({
      id: dateFormat,
      name: `${dateFormat}  (${dayjs(DATETIME_FORMAT_EXAMPLE_DATE).format(dateFormat)})`,
    }),
  );

  public readonly datePartDropdownOptions: IDropdownOption<EDatePart>[] = [
    {
      id: EDatePart.range,
      name: this.translate.instant('page.dataIntegrations.endpointParameters.datePart.range'),
    },
    {
      id: EDatePart.start,
      name: this.translate.instant('page.dataIntegrations.endpointParameters.datePart.start'),
    },
    {
      id: EDatePart.end,
      name: this.translate.instant('page.dataIntegrations.endpointParameters.datePart.end'),
    },
  ];

  public readonly shiftOutputDropdownOptions: IDropdownOption<EShiftOutput>[] = [
    {
      id: EShiftOutput.shiftDay,
      name: this.translate.instant('page.dataIntegrations.endpointParameters.shiftOutput.shiftDay'),
    },
    {
      id: EShiftOutput.shiftTimes,
      name: this.translate.instant('page.dataIntegrations.endpointParameters.shiftOutput.shiftTimes'),
    },
    {
      id: EShiftOutput.shift,
      name: this.translate.instant('page.dataIntegrations.endpointParameters.shiftOutput.shift'),
    },
  ];

  public readonly ESqlOperators = ESqlOperators;

  public readonly workspaceDropdownSettings: IDropdownSettings = {
    hasDelayedDefaultValue: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.workspace'),
  };

  public readonly paramLocationDropdownSettings: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.paramLocation'),
  };

  public readonly fieldDropdownSettings: IDropdownSettings = {
    enableSearchFilter: true,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.targetField'),
  };

  public readonly operatorDropdownSettings: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.operator'),
  };

  public readonly isRequiredDropdownSettings: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.isRequired'),
  };

  public readonly valueTypeDropdownSettings: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.valueType'),
  };

  public readonly dateFormatDropdownSettings: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.valueDateFormat'),
  };

  public readonly datePartDropdownSettings: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.datePart'),
  };

  public readonly shiftOutputDropdownSettings: IDropdownSettings = {
    enableSearchFilter: false,
    isClientSide: true,
    repositionDropdownList: true,
    singleSelection: true,
    text: this.translate.instant('field.shiftOutput'),
  };

  public readonly addEditForm = new FormGroup({
    key: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(100)],
    }),
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator(), Validators.maxLength(100)],
    }),
    paramLocation: new FormControl<IDropdownOption<EParamLocation>[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
    queryTemplate: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, TrimmedRequiredValidator()],
    }),
    tags: new FormArray<TTagFormGroupWithMeta>([]),
    workspaceId: new FormControl<IDropdownOption[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });

  public readonly queryTemplateSegments: Signal<IQueryTemplateSegment[]> = toSignal(
    this.addEditForm.controls.queryTemplate.valueChanges.pipe(
      startWith(this.addEditForm.controls.queryTemplate.value),
      map((value) => this.splitTemplateToSegments(this.beautifyTemplate(value ?? ''))),
    ),
    { initialValue: [] as IQueryTemplateSegment[] },
  );

  private readonly templateValue: Signal<string> = toSignal(
    this.addEditForm.controls.queryTemplate.valueChanges.pipe(startWith(this.addEditForm.controls.queryTemplate.value)),
    { initialValue: this.addEditForm.controls.queryTemplate.value ?? '' },
  );

  private readonly tagNames: WritableSignal<string[]> = signal<string[]>([]);

  public readonly needsSync: Signal<boolean> = computed(() => {
    const detected: string[] = [...this.extractUniqueTags(this.templateValue() ?? '')].sort();
    const existing: string[] = [...this.tagNames()].sort();

    return detected.length !== existing.length || detected.some((tag, index) => tag !== existing[index]);
  });

  private readonly paramLocationValue: Signal<IDropdownOption<EParamLocation>[]> = toSignal(
    this.addEditForm.controls.paramLocation.valueChanges.pipe(startWith(this.addEditForm.controls.paramLocation.value)),
    { initialValue: this.addEditForm.controls.paramLocation.value ?? [] },
  );

  private readonly keyValue: Signal<string> = toSignal(
    this.addEditForm.controls.key.valueChanges.pipe(startWith(this.addEditForm.controls.key.value)),
    { initialValue: this.addEditForm.controls.key.value ?? '' },
  );

  public readonly previewPrefix: Signal<string> = computed(() => {
    const key: string = (this.keyValue() ?? '').trim();
    const locationId: EParamLocation | undefined = this.paramLocationValue()?.[0]?.id;

    if (!key || !locationId) {
      return '';
    }

    switch (locationId) {
      case EParamLocation.query:
        return `?${key}=`;
      case EParamLocation.body:
        return `${key}:`;
      case EParamLocation.header:
        return `${key}=`;
      default:
        return '';
    }
  });

  constructor() {
    const { editData } = this.injectData;

    if (editData) {
      this.store.customFiltersLoaded$.pipe(filter(Boolean), take(1), takeUntilDestroyed()).subscribe(() => {
        this.addEditForm.controls.workspaceId.setValue([{ id: editData.workspaceId, name: '...' }]);
        this.addEditForm.controls.name.setValue(editData.name);
        this.addEditForm.controls.key.setValue(editData.key);
        this.addEditForm.controls.queryTemplate.setValue(editData.queryTemplate);
        this.addEditForm.controls.paramLocation.setValue(
          this.paramLocationDropdownOptions.filter(({ id }) => id === editData.paramLocation),
        );

        const detectedTags = this.extractUniqueTags(editData.queryTemplate);

        for (const tagName of detectedTags) {
          const existing = editData.tags?.find((t) => t.tag === tagName);
          this.addEditForm.controls.tags.push(this.buildTagGroup(tagName, existing));
        }

        this.refreshTagNames();
      });
    }

    this.addEditForm.controls.queryTemplate.valueChanges
      .pipe(debounceTime(AUTO_SYNC_IDLE_DELAY), distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(() => this.syncMappings());

    this.getWorkspaces();
    this.store.loadCustomFilters({ limit: 100 });

    this.store.afterCreateUpdateSuccess$.pipe(takeUntilDestroyed()).subscribe(() => {
      this.snackbarService.open(this.translate.instant('system.message.changesSuccessfully'), ESnackbar.success);
      this.matDialogRef.close();
    });

    if (!editData) {
      this.store.workspacesLoading$
        .pipe(
          filter((value: ELoadStatus) => value === ELoadStatus.success),
          take(1),
          takeUntilDestroyed(),
        )
        .subscribe(() => {
          const workspaces = this.store.workspaces();

          if (workspaces.length === 1) {
            this.addEditForm.controls.workspaceId.setValue([{ id: workspaces[0].id, name: workspaces[0].name }]);
          }
        });
    }
  }

  public getWorkspaces(search?: string): void {
    this.store.loadWorkspaces({
      fields: ['name'],
      sort: [{ active: 'name', direction: 'asc' }],
      ...(search ? { search: { searchedFields: ['name'], searchText: search } } : {}),
    });
  }

  public syncMappings(): void {
    this.syncTagsWithTemplate(this.addEditForm.controls.queryTemplate.value);
    this.refreshTagNames();
  }

  private refreshTagNames(): void {
    this.tagNames.set(this.addEditForm.controls.tags.controls.map((group) => group.controls.tag.value));
  }

  public onFormDialogSubmit(): void {
    this.addEditForm.controls.tags.controls.forEach((group) => group.markAllAsTouched());
    this.addEditForm.markAllAsTouched();

    if (!this.addEditForm.valid) {
      return;
    }

    const payload = this.formatPayload();

    if (this.injectData.editData?.id) {
      this.store.updateOne({ dto: payload, id: this.injectData.editData.id });
    } else {
      this.store.createOne(payload);
    }
  }

  private syncTagsWithTemplate(template: string | null | undefined): void {
    const detected = this.extractUniqueTags(template ?? '');
    const existingTags = this.addEditForm.controls.tags.controls.map((g) => g.controls.tag.value);

    for (let i = this.addEditForm.controls.tags.length - 1; i >= 0; i--) {
      if (!detected.includes(existingTags[i])) {
        this.addEditForm.controls.tags.removeAt(i);
      }
    }

    const remaining = this.addEditForm.controls.tags.controls.map((g) => g.controls.tag.value);

    for (const tagName of detected) {
      if (!remaining.includes(tagName)) {
        this.addEditForm.controls.tags.push(this.buildTagGroup(tagName));
      }
    }
  }

  private extractUniqueTags(template: string): string[] {
    if (!template) {
      return [];
    }

    const tokens: TMustacheToken[] = this.parseMustacheTokens(template);
    const result: string[] = [];
    const seen = new Set<string>();

    const walk = (nodes: TMustacheToken[]): void => {
      for (const node of nodes) {
        const [type, name] = node;

        if (type === 'name' || type === '&') {
          if (name && !seen.has(name)) {
            seen.add(name);
            result.push(name);
          }
        } else if ((type === '#' || type === '^') && Array.isArray(node[4])) {
          walk(node[4] as TMustacheToken[]);
        }
      }
    };

    walk(tokens);

    return result;
  }

  private parseMustacheTokens(template: string): TMustacheToken[] {
    try {
      return Mustache.parse(template) as unknown as TMustacheToken[];
    } catch {
      return [];
    }
  }

  private beautifyTemplate(template: string): string {
    if (!template?.trim()) {
      return template;
    }

    const placeholders: string[] = [];
    let transformed = '';
    let inString = false;

    for (let i = 0; i < template.length; ) {
      const ch = template[i];

      if (ch === '"' && !this.isCharEscaped(template, i)) {
        inString = !inString;
        transformed += ch;
        i++;

        continue;
      }

      if (template.startsWith('{{', i)) {
        const closeIdx = template.indexOf('}}', i + 2);

        if (closeIdx !== -1) {
          const original = template.slice(i, closeIdx + 2);
          const idx = placeholders.length;
          placeholders.push(original);
          const token = `__SCW_TAG_${idx}__`;
          transformed += inString ? token : `"${token}"`;
          i = closeIdx + 2;

          continue;
        }
      }

      transformed += ch;
      i++;
    }

    try {
      const parsed: unknown = JSON.parse(transformed);
      let result: string = JSON.stringify(parsed, null, 2);

      for (let n = 0; n < placeholders.length; n++) {
        const token = `__SCW_TAG_${n}__`;
        result = result.split(`"${token}"`).join(placeholders[n]);
        result = result.split(token).join(placeholders[n]);
      }

      return result;
    } catch {
      return template;
    }
  }

  private isCharEscaped(str: string, pos: number): boolean {
    let count = 0;

    for (let i = pos - 1; i >= 0 && str[i] === '\\'; i--) {
      count++;
    }

    return count % 2 === 1;
  }

  private splitTemplateToSegments(template: string): IQueryTemplateSegment[] {
    if (!template) {
      return [];
    }

    const tokens: TMustacheToken[] = this.parseMustacheTokens(template);

    if (tokens.length === 0) {
      return [{ type: 'text', value: template }];
    }

    return this.tokensToSegments(tokens);
  }

  private tokensToSegments(tokens: TMustacheToken[]): IQueryTemplateSegment[] {
    return tokens.flatMap((node) => this.tokenToSegments(node));
  }

  private tokenToSegments(node: TMustacheToken): IQueryTemplateSegment[] {
    const [type, value] = node;

    if (type === '#' || type === '^') {
      return this.sectionToSegments(node, type);
    }

    const formatter = SIMPLE_TOKEN_FORMATTERS[type];

    return formatter ? [formatter(value)] : [];
  }

  private sectionToSegments(node: TMustacheToken, marker: '#' | '^'): IQueryTemplateSegment[] {
    const [, value] = node;
    const childTokens = Array.isArray(node[4]) ? (node[4] as TMustacheToken[]) : [];

    return [
      { type: 'tag', value: `{{${marker}${value}}}` },
      ...this.tokensToSegments(childTokens),
      { type: 'tag', value: `{{/${value}}}` },
    ];
  }

  private getTagInitialOptions(existing?: IUrlParamsTemplateTag): ITagInitialOptions {
    const operatorOptionsForField = existing
      ? (this.operatorOptionsByField().get(existing.field) ?? this.emptyOperatorOptions)
      : this.emptyOperatorOptions;

    const isExistingShiftTimes = existing?.shiftOutput === EShiftOutput.shiftTimes;
    const dateFormatOptionsForExisting = isExistingShiftTimes
      ? this.dateTimeFormatDropdownOptions
      : this.dateFormatDropdownOptions;
    const defaultDateFormatForExisting = isExistingShiftTimes
      ? DEFAULT_ENDPOINT_PARAMETER_DATETIME_FORMAT
      : DEFAULT_ENDPOINT_PARAMETER_DATE_FORMAT;

    return {
      dateFormatOption: dateFormatOptionsForExisting.filter(
        ({ id }) => id === (existing?.dateFormat ?? defaultDateFormatForExisting),
      ),
      datePartOption: this.datePartDropdownOptions.filter(({ id }) => id === (existing?.datePart ?? EDatePart.range)),
      fieldOption: existing ? this.fieldDropdownOptions().filter(({ id }) => id === existing.field) : [],
      isRequiredOption: this.isRequiredDropdownOptions.filter(({ id }) => id === (existing?.isRequired ?? false)),
      operatorOption: existing
        ? operatorOptionsForField.filter(({ id }) => id === (existing.operator as ESqlOperators))
        : [],
      shiftOutputOption: this.shiftOutputDropdownOptions.filter(
        ({ id }) => id === (existing?.shiftOutput ?? EShiftOutput.shiftDay),
      ),
      valueTypeOption: this.valueTypeDropdownOptions.filter(({ id }) => id === (existing?.valueType ?? EValueType.raw)),
    };
  }

  private buildTagGroup(tagName: string, existing?: IUrlParamsTemplateTag): TTagFormGroupWithMeta {
    const {
      fieldOption,
      operatorOption,
      isRequiredOption,
      valueTypeOption,
      dateFormatOption,
      datePartOption,
      shiftOutputOption,
    } = this.getTagInitialOptions(existing);

    const group = new FormGroup<ITagFormGroup>({
      dateFormat: new FormControl<IDropdownOption<string>[]>(dateFormatOption, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      datePart: new FormControl<IDropdownOption<EDatePart>[]>(datePartOption, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      field: new FormControl<IDropdownOption<string>[]>(fieldOption, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      isRequired: new FormControl<IDropdownOption<boolean>[]>(isRequiredOption, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      operator: new FormControl<IDropdownOption<ESqlOperators>[]>(operatorOption, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      shiftOutput: new FormControl<IDropdownOption<EShiftOutput>[]>(shiftOutputOption, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      splitBy: new FormControl(existing?.splitBy ?? ',', {
        nonNullable: true,
        validators: [Validators.maxLength(20)],
      }),
      tag: new FormControl(tagName, { nonNullable: true, validators: [Validators.required] }),
      valuePrefix: new FormControl(existing?.valuePrefix ?? '', {
        nonNullable: true,
        validators: [Validators.maxLength(10)],
      }),
      valueSuffix: new FormControl(existing?.valueSuffix ?? '', {
        nonNullable: true,
        validators: [Validators.maxLength(10)],
      }),
      valueType: new FormControl<IDropdownOption<EValueType>[]>(valueTypeOption, {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });

    group.controls.field.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      const currentOperator = group.controls.operator.value?.[0];
      const newOptions = this.operatorOptionsFor(group.controls.field.value?.[0]?.id);

      if (
        !currentOperator?.id ||
        !newOptions.some(({ id, name }) => id === currentOperator.id && name === currentOperator.name)
      ) {
        group.controls.operator.reset([]);
      }

      this.syncValueTypeControl(group);
      this.syncDateControls(group);
    });

    group.controls.operator.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.syncValueTypeControl(group);
      this.syncDateControls(group);
    });

    group.controls.shiftOutput.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.syncValueTypeControl(group);
      this.syncDateControls(group);
    });

    this.syncValueTypeControl(group);
    this.syncDateControls(group);

    return Object.assign(group, this.buildTagGroupMeta(group));
  }

  private buildTagGroupMeta(group: FormGroup<ITagFormGroup>): Omit<TTagFormGroupWithMeta, keyof FormGroup> {
    const fieldSignal = toSignal(group.controls.field.valueChanges, {
      initialValue: group.controls.field.value,
      injector: this.injector,
    });
    const operatorSignal = toSignal(group.controls.operator.valueChanges, {
      initialValue: group.controls.operator.value,
      injector: this.injector,
    });
    const datePartSignal = toSignal(group.controls.datePart.valueChanges, {
      initialValue: group.controls.datePart.value,
      injector: this.injector,
    });
    const shiftOutputSignal = toSignal(group.controls.shiftOutput.valueChanges, {
      initialValue: group.controls.shiftOutput.value,
      injector: this.injector,
    });

    const isInOperator = computed(() => operatorSignal()?.[0]?.id === ESqlOperators.$in);
    const isDateField = computed(() => this.isDateFieldId(fieldSignal()?.[0]?.id));
    const isShiftPhraseOperator = computed(() => {
      const operator = operatorSignal()?.[0]?.id;

      return operator === ESqlOperators.$currentShift || operator === ESqlOperators.$previousShift;
    });
    const isShiftOutputVisible = computed(() => isDateField() && isShiftPhraseOperator());
    const isShiftTimesSelected = computed(
      () => isShiftPhraseOperator() && shiftOutputSignal()?.[0]?.id === EShiftOutput.shiftTimes,
    );
    const isDatePartVisible = computed(
      () => isDateField() && (operatorSignal()?.[0]?.id === ESqlOperators.$between || isShiftTimesSelected()),
    );
    const isSplitByVisible = computed(
      () => isInOperator() || (isDatePartVisible() && datePartSignal()?.[0]?.id === EDatePart.range),
    );

    return {
      availableDateFormats: computed(() =>
        isShiftTimesSelected() ? this.dateTimeFormatDropdownOptions : this.dateFormatDropdownOptions,
      ),
      availableValueTypes: computed(() =>
        this.valueTypeOptionsFor(
          fieldSignal()?.[0]?.id,
          isShiftPhraseOperator() && shiftOutputSignal()?.[0]?.id === EShiftOutput.shift,
        ),
      ),
      isDateField,
      isDatePartVisible,
      isInOperator,
      isShiftOutputVisible,
      isShiftPhraseOperator,
      isShiftTimesSelected,
      isSplitByVisible,
      operatorOptions: computed(() => this.operatorOptionsFor(fieldSignal()?.[0]?.id)),
    };
  }

  private isDateFieldId(fieldId: string | undefined): boolean {
    if (!fieldId || !(fieldId in ADVANCED_FILTER_FIELDS)) {
      return false;
    }

    return ADVANCED_FILTER_FIELDS[fieldId as keyof typeof ADVANCED_FILTER_FIELDS].some(
      ({ type }) => type === EFieldTypes.date,
    );
  }

  private syncDateControls(group: FormGroup<ITagFormGroup>): void {
    const isDateField: boolean = this.isDateFieldId(group.controls.field.value?.[0]?.id);
    const operatorId = group.controls.operator.value?.[0]?.id;
    const isRangeOperator: boolean = operatorId === ESqlOperators.$between;
    const isShiftPhraseOperator: boolean =
      operatorId === ESqlOperators.$currentShift || operatorId === ESqlOperators.$previousShift;
    const shiftOutputId = group.controls.shiftOutput.value?.[0]?.id;
    const isShiftOutputAsShift: boolean = isShiftPhraseOperator && shiftOutputId === EShiftOutput.shift;
    const isShiftTimesSelected: boolean = isShiftPhraseOperator && shiftOutputId === EShiftOutput.shiftTimes;

    this.setControlEnabledState(group.controls.dateFormat, isDateField && !isShiftOutputAsShift);
    this.setControlEnabledState(group.controls.datePart, isDateField && (isRangeOperator || isShiftTimesSelected));
    this.setControlEnabledState(group.controls.shiftOutput, isDateField && isShiftPhraseOperator);
    this.syncDateFormatControl(group.controls.dateFormat, isShiftTimesSelected);
  }

  /** Swaps between date-only and datetime format options as `shiftOutput` toggles to/from `shiftTimes`,
   * resetting the selected format when it doesn't belong to the newly-applicable list. */
  private syncDateFormatControl(control: FormControl<IDropdownOption<string>[]>, isShiftTimesSelected: boolean): void {
    const allowed = isShiftTimesSelected ? this.dateTimeFormatDropdownOptions : this.dateFormatDropdownOptions;
    const current = control.value?.[0]?.id;

    if (current && !allowed.some(({ id }) => id === current)) {
      control.setValue(
        allowed.filter(
          ({ id }) =>
            id ===
            (isShiftTimesSelected
              ? DEFAULT_ENDPOINT_PARAMETER_DATETIME_FORMAT
              : DEFAULT_ENDPOINT_PARAMETER_DATE_FORMAT),
        ),
      );
    }
  }

  private setControlEnabledState(control: FormControl<unknown[]>, isEnabled: boolean): void {
    if (isEnabled === control.enabled) {
      return;
    }

    if (isEnabled) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  private operatorOptionsFor(fieldId: string | undefined): IDropdownOption<ESqlOperators>[] {
    if (!fieldId) {
      return this.emptyOperatorOptions;
    }

    return this.operatorOptionsByField().get(fieldId) ?? this.emptyOperatorOptions;
  }

  private valueTypeOptionsFor(
    fieldId: string | undefined,
    isShiftOutputAsShift = false,
  ): IDropdownOption<EValueType>[] {
    // "Shift" output has a real display name (the shift's name) unlike a literal date, so both raw/display apply.
    if (isShiftOutputAsShift || !fieldId || !(fieldId in ADVANCED_FILTER_FIELD_VALUE_TYPES)) {
      return this.valueTypeDropdownOptions;
    }

    const allowed = ADVANCED_FILTER_FIELD_VALUE_TYPES[fieldId as keyof typeof ADVANCED_FILTER_FIELD_VALUE_TYPES];

    return this.valueTypeDropdownOptions.filter(({ id }) => allowed.includes(id));
  }

  private syncValueTypeControl(group: FormGroup<ITagFormGroup>): void {
    const operatorId = group.controls.operator.value?.[0]?.id;
    const isShiftOutputAsShift =
      (operatorId === ESqlOperators.$currentShift || operatorId === ESqlOperators.$previousShift) &&
      group.controls.shiftOutput.value?.[0]?.id === EShiftOutput.shift;
    const control = group.controls.valueType;
    const allowed = this.valueTypeOptionsFor(group.controls.field.value?.[0]?.id, isShiftOutputAsShift);

    if (allowed.length === 1) {
      control.setValue(allowed);
      control.disable();

      return;
    }

    control.enable();

    const current = control.value?.[0]?.id;

    if (current && !allowed.some(({ id }) => id === current)) {
      control.reset([]);
    }
  }

  private formatPayload(): IUrlParamsTemplateAddPayload {
    const { value } = this.addEditForm;

    const tags: IUrlParamsTemplateTagPayload[] = this.addEditForm.controls.tags.controls.map((group) => {
      const operatorId = group.controls.operator.value[0].id;
      const splitByRaw = FormUtilitiesService.trimIfString(group.controls.splitBy.value);

      return {
        dateFormat: group.controls.dateFormat.enabled ? (group.controls.dateFormat.value[0]?.id ?? null) : null,
        datePart: group.controls.datePart.enabled ? (group.controls.datePart.value[0]?.id ?? null) : null,
        field: group.controls.field.value[0].id,
        isRequired: group.controls.isRequired.value[0].id,
        operator: operatorId,
        shiftOutput: group.controls.shiftOutput.enabled ? (group.controls.shiftOutput.value[0]?.id ?? null) : null,
        splitBy: group.isSplitByVisible() && splitByRaw ? splitByRaw : null,
        tag: group.controls.tag.value,
        valuePrefix: group.controls.valuePrefix.value || null,
        valueSuffix: group.controls.valueSuffix.value || null,
        valueType: group.controls.valueType.value[0].id,
      };
    });

    return {
      key: FormUtilitiesService.trimIfString(value.key!),
      name: FormUtilitiesService.trimIfString(value.name!),
      paramLocation: value.paramLocation![0].id,
      queryTemplate: FormUtilitiesService.trimIfString(value.queryTemplate!),
      tags,
      workspaceId: value.workspaceId![0].id as number,
    };
  }
}
